import React from 'react';
import { cn } from '../lib/utils';
import { EmptyValue } from './empty-value';

export interface KeyFact {
  id: string;
  label: string;
  value?: React.ReactNode;
}

export interface KeyFactGroup {
  id: string;
  title: string;
  facts: KeyFact[];
}

export interface KeyFactsProps {
  facts?: KeyFact[];
  /** Titled groups follow any ungrouped facts. Empty groups are omitted. */
  groups?: KeyFactGroup[];
  variant?: 'stacked' | 'inline';
  columns?: 1 | 2 | 3 | 5;
  hideEmpty?: boolean;
  /** Strong values are opt-in for record overviews. */
  emphasis?: 'default' | 'strong';
  className?: string;
}

const columnClasses = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  5: 'grid-cols-1 min-[401px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-5',
};

function isEmpty(value: React.ReactNode) {
  return (
    value == null ||
    typeof value === 'boolean' ||
    (typeof value === 'string' && value.trim() === '')
  );
}

/** Values remain caller-formatted. Zero is a recorded value, not an empty one. */
export function KeyFacts({
  facts = [],
  groups = [],
  variant = 'stacked',
  columns = 3,
  hideEmpty = false,
  emphasis = 'default',
  className,
}: KeyFactsProps) {
  const visible = (items: KeyFact[]) =>
    hideEmpty ? items.filter((fact) => !isEmpty(fact.value)) : items;
  const visibleFacts = visible(facts);
  const visibleGroups = groups
    .map((group) => ({ ...group, facts: visible(group.facts) }))
    .filter((group) => group.facts.length > 0);
  if (visibleFacts.length === 0 && visibleGroups.length === 0) return null;

  const renderFacts = (items: KeyFact[], listClassName?: string) => (
    <dl
      className={cn(
        'min-w-0 font-body',
        variant === 'inline' ? 'divide-y divide-border' : cn('grid gap-4', columnClasses[columns]),
        listClassName,
      )}
    >
      {items.map((fact) => (
        <div
          key={fact.id}
          className={cn(
            'min-w-0',
            variant === 'inline'
              ? 'grid grid-cols-1 gap-1 py-2 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]'
              : 'space-y-1',
          )}
        >
          <dt className="min-w-0 break-words text-caption font-normal text-text-muted [overflow-wrap:anywhere]">
            {fact.label}
          </dt>
          <dd
            className={cn(
              'm-0 min-w-0 break-words text-body [&_[data-ui=badge]]:self-center tabular-nums text-text-heading [overflow-wrap:anywhere]',
              emphasis === 'strong' ? 'font-semibold' : 'font-medium',
            )}
          >
            {isEmpty(fact.value) ? (
              <EmptyValue
                label="Not recorded"
                className="[&>span[aria-hidden]]:hidden [&>.sr-only]:not-sr-only"
              />
            ) : (
              fact.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );

  if (visibleGroups.length === 0) return renderFacts(visibleFacts, className);
  return (
    <div className={cn('min-w-0 space-y-4', className)}>
      {visibleFacts.length > 0 && renderFacts(visibleFacts)}
      {visibleGroups.map((group) => (
        <section key={group.id} aria-label={group.title} className="min-w-0 space-y-4">
          <h3 className="min-w-0 break-words font-body text-card-title text-text-heading [overflow-wrap:anywhere]">
            {group.title}
          </h3>
          {renderFacts(group.facts)}
        </section>
      ))}
    </div>
  );
}
